import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { range } from 'lodash';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { getFloorItemType } from 'src/store/view-2d/selectors';
import { useOpenFilter } from 'src/hooks/app';
import { EFloorType } from 'src/store/view-2d/reducer';
import { CrossTableContent } from 'src/components/CrosstableContent';
import { ApartmentSidebar } from 'src/components/CrosstableContent/components/AppartmentSidebar';
import { TypeView2d } from 'src/components/TypeView2d';
import { SelectFloorItemType } from 'src/components/SelectFloorItemType';
import {
  useTranslations,
  useGetComplexStatuses,
  useBuildType,
  useLanguageState,
  useGetAuthData,
  useIsMobile,
  useTypeDisplayedSpinner,
} from 'src/hooks';
import { locals } from 'src/constants/locals';
import { CrosstableContentTable } from 'src/components/CrosstableContentTable';
import { getUserData } from 'src/store/requests/user/selector';
import { TypeOfRole } from 'src/typings/user';
import { ButtonApp } from 'src/components/ApartmentPageV2/Button';
import { useTable } from 'src/hooks/table/useTable';
import { usePlacementTypeViews } from 'src/hooks/table/usePlacementTypeViews';
import Popover from '@mui/material/Popover';
import { IVisibleConfig } from 'src/store/type-displayed-spinner/types';
import { ReportIcon } from 'src/components/ReportIcon';
import { ExportButton } from 'src/typings/table';
import { getIsOpenAppartSidebar, getCrmData } from 'src/store/app/selectors';
import { getBitrixProject } from 'src/api';
import { CRMNames } from 'src/typings/app';
import isEmpty from 'lodash/isEmpty';
import { TypesOfSpinner } from 'src/typings/types-of-spinner';
import { AppHeaderLogo } from 'src/components/ApartmentPageV2/AppHeader/ComponentLogo';
import { requestSelectors } from 'src/store/requests/selectors';
import { IFilterAllData } from 'src/typings/filter';
import { useAppMediaQuery } from 'src/hooks/app/useAppMediaQuery';
import { useComplex } from 'src/hooks/app/use-complex';
import { ApartmentSidebarCross } from 'src/components/CrosstableContent/components/AppartmentSidebarCross';
import { useControlCrossTable, useCrossTableState } from './hooks';
import { useStyles } from './styles';

export const CrossTableContainer: React.FC = () => {
  const { complex } = useComplex();
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const styles = useStyles({});
  const { getTranslations } = useTranslations();
  const floorItemType = useSelector(getFloorItemType);
  const { isCrossTable } = useBuildType();
  const { currentAppLanguage } = useLanguageState();
  const { is } = useAppMediaQuery();
  const isSmallScreen = is('(max-width:600px)');
  const {
    refCrossTable,
    measuredRef,
  } = useControlCrossTable();
  const { isOpen } = useOpenFilter();
  const { isMobileScreen } = useIsMobile();
  const { isLoading } = useGetComplexStatuses();
  const {
    floorList,
    view2dData,
    view2dDataList,
    view2dDataObject,
  } = useCrossTableState();
  const user = useSelector(getUserData);
  const crmData = useSelector(getCrmData);
  const [visibleConfig, setVisibleConfig] = useState<IVisibleConfig>({
    detailedSubView: { isEnabled: false, sortOrder: 0 },
    simpleSubView: { isEnabled: false, sortOrder: 1 },
    listSubView: { isEnabled: false, sortOrder: 2 },
  });
  const [showSelector, setShowSelector] = useState<boolean>(false);
  const {
    exportExcelFile,
    getWorkbook,
    getExportPdfData,
    setExcelExportInProgress,
    applyAndDownloadReport,
    excelExportInProgress,
    pdfExportInProgress,
    tableApiRef,
    applyApi,
  } = useTable();
  const [isShowDealDataForManagers, setIsShowDealDataForManagers] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const { currentListSubView, currentView, enabledSubViewsCount } = usePlacementTypeViews();
  const listViewRoles = [TypeOfRole.superadmin, TypeOfRole.supervisor];
  const managersList = [TypeOfRole.manager];
  const isOpenAppartSidebar = useSelector(getIsOpenAppartSidebar);
  const spinnerData = useSelector(requestSelectors.spinner.getSpinnerViewData);
  const { getSelectedViewId, is2dView } = useTypeDisplayedSpinner();
  // Guards async handlers (excel export) against setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (floorItemType === EFloorType.list && tableApiRef.current) {
      applyApi();
    }
  }, [floorItemType]);

  useEffect(() => {
    // Cancellation: the container unmounts on view switches (Dashboard <->
    // Floors/3D) while this request is in flight — a late .then used to
    // setState on the unmounted component.
    let cancelled = false;
    if (crmData) {
      if (crmData.crm === CRMNames.bitrix && user) {
        getBitrixProject(jwt, String(crmData.projectId))
          .then((res) => {
            if (cancelled) return;
            if (user.role && managersList.includes(user.role?.name)) {
              setIsShowDealDataForManagers(res.data.frontConfig.dealConfig.isShowDealDataForManagers);
            } else {
              setIsShowDealDataForManagers(true);
            }
          });
      } else {
        setIsShowDealDataForManagers(true);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [crmData, user]);

  const listIsVisible = useMemo(() => {
    if (isCrossTable && user && user.role) {
      return listViewRoles.includes(user.role.name);
    }
    return true;
  }, [user]);

  useEffect(() => {
    if (currentView) {
      const tempConfig = {};
      currentView?.subViews?.forEach((sub) => {
        const name: string = sub.type.charAt(0).toLowerCase() + sub.type.slice(1);
        // @ts-ignore
        tempConfig[name] = { isEnabled: sub.isEnabled, sortOrder: sub.sortOrder };
      });
      setVisibleConfig(tempConfig as IVisibleConfig);
      // @ts-ignore
      setShowSelector(Object.keys(tempConfig).filter((key) => tempConfig[key].isEnabled).length > 1);
    }
  }, [currentView]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (currentListSubView && enabledSubViewsCount === 1) {
      const activeBtn = currentListSubView.placementReportSetting.exportButtons.find((btn) => btn.isEnabled)?.type;
      switch (activeBtn) {
        case ExportButton.excel:
          exportExcelFileHandler(view2dDataObject);
          break;
        case ExportButton.pdf:
          getExportPdfData(view2dDataObject);
          break;
        default:
          break;
      }
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const exportExcelFileHandler = (data: {[key: string]: IFilterAllData}) => {
    const { workBook, workBookOptions } = getWorkbook();
    const sheet = workBook.addWorksheet('Report', workBookOptions);
    setExcelExportInProgress(true);
    if (complex) {
      exportExcelFile(data, sheet, workBook, undefined, complex)
        .then(() => {
          // The download itself must finish; only the state update needs the
          // component to still be mounted.
          applyAndDownloadReport(workBook);
          if (mountedRef.current) {
            setExcelExportInProgress(false);
          }
        });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (view2dData === null) {
    return (
      <div className={styles.noDisplayData}>
        <p>{getTranslations(locals.appTranslations.appNoDataToDisplay)}</p>
        <p>{getTranslations(locals.appTranslations.appPleaseStartFiltering)}</p>
      </div>
    );
  }

  const open = Boolean(anchorEl);
  const popoverId = open ? 'simple-popover' : undefined;

  return (
    <div className={`${styles.crossTableContainer} ${!isOpen && !isMobileScreen && styles.closedFilters}`}>
      {(!isMobileScreen || (isMobileScreen && !isOpenAppartSidebar)) && (
        <Box
          className={styles.crossTableContainerWrapper}
          mb={isCrossTable ? 4 : 0}
          ml={!isOpen && isCrossTable ? '-60px' : 0}
          pr={6}
          flexDirection={isSmallScreen ? 'column' : 'row'}
        >
          {((isCrossTable && !isOpen && (isEmpty(spinnerData?.complex) || (!isEmpty(spinnerData?.complex) && getSelectedViewId !== TypesOfSpinner.viewSpinner)))) && (
            <div className={styles.logo}>
              <AppHeaderLogo isVisible />
            </div>
          )}
          <Box display="flex" flexDirection="row" gap="20px" height="44px" justifyContent="center" alignItems="center">
            {showSelector && currentView && (
              <Box py={1}>
                <SelectFloorItemType
                  lang={currentAppLanguage}
                  placementsReport={currentView}
                  visibleConfig={visibleConfig}
                />
              </Box>
            )}
            {floorItemType === EFloorType.mini && (
              <Box py={1}>
                <TypeView2d />
              </Box>
            )}
            {floorItemType === EFloorType.list && listIsVisible && (
              <div>
                {currentListSubView?.placementReportSetting.exportButtons.some((b) => b.isEnabled) && (
                  <>
                    <ButtonApp
                      startIcon={enabledSubViewsCount === 1 ? <ReportIcon isLoading={excelExportInProgress || pdfExportInProgress} /> : null}
                      color="primary"
                      text={
                        currentListSubView?.placementReportSetting.downloadOrChooseExportPlacementsReportName.translates?.[currentAppLanguage]
                        || currentListSubView?.placementReportSetting.downloadOrChooseExportPlacementsReportName.systemName
                      }
                      onClick={(e) => handleClick(e)}
                      className={styles.exportBtn}
                      isCapitalize
                      variant="contained"
                    />
                    <Popover
                      id={popoverId}
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            overflow: 'hidden',
                          },
                        },
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        {currentListSubView?.placementReportSetting.exportButtons.map((button) => {
                          return button.isEnabled && (
                            <Box height="40px" key={button.id}>
                              <ButtonApp
                                startIcon={<ReportIcon isLoading={button.type === ExportButton.excel ? excelExportInProgress : pdfExportInProgress} />}
                                color="secondary"
                                style={{
                                  padding: '8px 16px',
                                }}
                                disabled={button.type === ExportButton.excel ? excelExportInProgress : pdfExportInProgress}
                                onClick={() => (button.type === ExportButton.pdf ? getExportPdfData(view2dDataObject) : exportExcelFileHandler(view2dDataObject))}
                              >
                                {(currentAppLanguage && button.name.translates?.[currentAppLanguage]) || button.name.systemName}
                              </ButtonApp>
                            </Box>
                          );
                        })}
                      </Box>
                    </Popover>
                  </>
                )}
              </div>
            )}
          </Box>
        </Box>
      )}
      {isLoading && (
        <Box
          display="flex"
          justifyContent="flex-end"
          pr={6}
          flexWrap="wrap"
          my={1}
        >
          {range(4).map((item) => (
            <Box key={item} display="flex" mr={2}>
              <Skeleton variant="rectangular" width={20} height={20} />
              <Box ml={1} width="50px">
                <Skeleton variant="text" />
              </Box>
            </Box>
          ))}

        </Box>
      )}
      <div
        ref={measuredRef}
        className={styles.crossTable}
        // style={{ maxHeight: crossTableHeight || 'none', overflow: crossTableHeight ? 'unset' : 'hidden' }}
      >
        {
          refCrossTable
          && Boolean(floorList.length)
          && view2dData
          && floorItemType !== EFloorType.list && (
            <>
              <CrossTableContent
                view2dData={view2dData}
                floorList={floorList}
              />
            </>
          )
        }
        {floorItemType === EFloorType.list && (
          <CrosstableContentTable
            view2dDataList={view2dDataList}
          />
        )}
        {isOpenAppartSidebar && !isCrossTable && (
          <ApartmentSidebar is2dView={is2dView} />
        )}
        {isOpenAppartSidebar && isCrossTable && (
          <ApartmentSidebarCross
            showDealDetails={isShowDealDataForManagers}
          />
        ) }
      </div>
    </div>
  );
};
